import * as v from "valibot";
import {
  ErrorResponseSchema,
  type ApiRequest,
  type AdminRequest,
} from "../../shared/api";

const connectionProblem = "حدثت مشكلة في الاتصال";
// Without this, Cloudflare Access answers an admin request with a redirect to
// its sign-in page, which fetch can't follow, rather than a 401.
const requestedBy = { "X-Requested-With": "XMLHttpRequest" };

export class ApiError extends Error {
  status: number;
  // Seconds until the server accepts the same request again.
  retryAfter?: number;
  constructor(message: string, status: number, retryAfter?: number) {
    super(message);
    this.status = status;
    this.retryAfter = retryAfter;
  }
}
// Whether the server gave its own reason, rather than the request going missing.
export const serverExplained = (error: unknown): error is ApiError =>
  error instanceof ApiError && error.message !== connectionProblem;
// Leaving a page cancels its request; a timeout is a connection problem like any other.
const cancelled = (signal?: AbortSignal) =>
  signal?.aborted && signal.reason?.name !== "TimeoutError";

export async function requestApi<T extends v.GenericSchema>(
  path: string,
  schema: T,
  body?: ApiRequest | AdminRequest,
  signal?: AbortSignal,
): Promise<v.InferOutput<T>> {
  let response: Response;
  try {
    response = await fetch(
      path,
      body
        ? {
            method: "POST",
            headers: { ...requestedBy, "Content-Type": "application/json" },
            body: JSON.stringify(body),
            signal,
          }
        : { cache: "no-store", headers: requestedBy, signal },
    );
  } catch (error) {
    if (cancelled(signal)) throw error;
    throw new ApiError(connectionProblem, 503);
  }
  let data: unknown;
  try {
    data = await response.json();
  } catch (error) {
    // Preserve cancellation instead of turning an aborted body read into a 503.
    if (cancelled(signal)) throw error;
    throw new ApiError(connectionProblem, response.ok ? 503 : response.status);
  }
  if (!response.ok) {
    const error = v.safeParse(ErrorResponseSchema, data);
    throw new ApiError(
      error.success ? error.output.error : connectionProblem,
      response.status,
      Number(response.headers.get("Retry-After")) || undefined,
    );
  }
  const result = v.safeParse(schema, data);
  if (!result.success) throw new ApiError(connectionProblem, 503);
  return result.output;
}
export const api = <T extends v.GenericSchema>(
  schema: T,
  body?: ApiRequest,
  query = "",
  signal?: AbortSignal,
) => requestApi("/api/app" + query, schema, body, signal);
